import { NotificationModal } from '@components/ui';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { IconButton, List, ListItem, ListItemText, TextField, Tooltip as MuiTooltip, Switch } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DownloadSubcontractorPdfButton } from './DownloadSubcontractorPdfButton';

import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { ISignatureRequest } from '@dieterApi/signature/useSignatureRequestCreateMutation';
import { UserFile, UserQuestionnaireApplicationTopic } from '@dieterApi/user/useUserQuery';
import { ContractDirection } from './typesAndEnums';

export interface PartnerData {
  partnerName: string;
  address?: string;
  details?: string;
  contractType?: string;
  contractFile?: UserFile;
  signedContractFile?: UserFile;
  contractLocked?: boolean;
  contractSigned?: boolean;
  signatureRequest?: ISignatureRequest;
  contract?: UserQuestionnaireApplicationTopic;
  direction?: ContractDirection;
  index: string;
  isSubContractor?: boolean;
  toms?: UserFile;
}

interface UserValue {
  key: string;
  value: string;
  index: string;
}

interface User {
  id?: string;
  userValues?: UserValue[];
  [key: string]: any;
}

interface ManageSubcontractorsModalProps {
  open: boolean;
  onClose: () => void;
  mainPartner: { partnerName: string; index: string };
  partnerData: PartnerData[];
  user: User;
  addUserValue: (args: any) => Promise<any>;
  deleteUserValue: (args: any) => Promise<any>;
  loading: boolean;
  setEditAddressModalOpen: (open: boolean) => void;
  setCurrentAddressMeta: (meta: any) => void;
  setAddressDraft: (draft: string) => void; // Optional prop to set address draft
  getPartnerTableMetaByIndex: (index: string) => any;
}

// Helper: get all partners with direction 'Ich bin der Auftraggeber'
function getPrincipalPartners(partnerData: PartnerData[]): PartnerData[] {
  return partnerData.filter((p) => p.direction === 'Ich bin der Auftraggeber' && p.index && p.partnerName);
}

// Helper: get details per subcontractor for a given main partner (from userValues)
function getSubcontractorDetailsMap(userValues: UserValue[] | undefined, mainIndex: string): Record<string, string> {
  if (!userValues) return {};
  const uv = userValues.find((uv) => uv.key === 'DATA_PROCESSOR_SUBCONTRACTOR_DETAILS' && uv.index === mainIndex);
  if (!uv) return {};
  try {
    const obj = JSON.parse(uv.value);
    if (typeof obj === 'object' && !Array.isArray(obj) && obj !== null) return obj;
    return {};
  } catch {
    return {};
  }
}

// Helper: get all subprocessor indices for a given main partner (from userValues)
function getSubcontractorIndices(userValues: UserValue[] | undefined, mainIndex: string): string[] {
  if (!userValues) return [];
  const uv = userValues.find((uv) => uv.key === 'DATA_PROCESSOR_HASSUBCONTRACTOR' && uv.index === mainIndex);
  if (!uv) return [];
  try {
    const arr = JSON.parse(uv.value);
    if (Array.isArray(arr)) {
      return arr.filter((v) => typeof v === 'string');
    }
    return [];
  } catch {
    return [];
  }
}

export function ManageSubcontractorsModal({
  open,
  onClose,
  mainPartner,
  partnerData,
  user,
  addUserValue,
  deleteUserValue,
  loading,
  setEditAddressModalOpen,
  setCurrentAddressMeta,
  setAddressDraft,
  getPartnerTableMetaByIndex,
}: ManageSubcontractorsModalProps) {
  const [localLoading, setLocalLoading] = useState<string | null>(null); // index of partner being toggled
  const [detailsDraft, setDetailsDraft] = useState<Record<string, string>>({});
  const { userValues } = useUserValuesData(); // Get fresh userValues from the hook
  const mainIndex = mainPartner?.index;

  // Sync detailsDraft from userValues whenever they change
  useEffect(() => {
    if (!mainIndex) return;
    const map = getSubcontractorDetailsMap(userValues, mainIndex);
    setDetailsDraft(map);
  }, [userValues, mainIndex]);

  // Memoize derived state to ensure updates when dependencies change
  const principalPartners = useMemo(() => getPrincipalPartners(partnerData), [partnerData]);
  const subIndices = useMemo(() => getSubcontractorIndices(userValues, mainIndex), [userValues, mainIndex]);
  // Get selected partners for PDF (with details)
  const selectedPartners = useMemo(
    () =>
      principalPartners
        .filter((p) => subIndices.includes(p.index))
        .map((p) => ({ ...p, details: detailsDraft[p.index] || '' })),
    [principalPartners, subIndices, detailsDraft]
  );

  const handleDetailsBlur = async (subIndex: string, value: string) => {
    const current = getSubcontractorDetailsMap(userValues, mainIndex);
    const updated = { ...current, [subIndex]: value };
    await addUserValue({
      variables: {
        key: 'DATA_PROCESSOR_SUBCONTRACTOR_DETAILS',
        value: JSON.stringify(updated),
        index: mainIndex,
      },
    });
  };

  const handleToggle = async (subIndex: string, checked: boolean) => {
    setLocalLoading(subIndex);
    try {
      // Get current set from latest userValues
      const current = new Set(getSubcontractorIndices(userValues, mainIndex));
      if (checked) {
        current.add(subIndex);
      } else {
        current.delete(subIndex);
      }
      // Ensure unique values
      const arr = Array.from(current);
      await addUserValue({
        variables: {
          key: 'DATA_PROCESSOR_HASSUBCONTRACTOR',
          value: JSON.stringify(arr),
          index: mainIndex,
        },
        // context: { userId: user?.id || '' },
      });
    } catch (error) {
      console.error('Failed to toggle subcontractor:', error);
    } finally {
      setLocalLoading(null);
    }
  };

  return (
    <NotificationModal open={open} onClose={onClose} maxWidth="800px">
      <div className="flex flex-col gap-[10px] p-8">
        <h3 className="font-medium text-xl">Subunternehmer verwalten</h3>
        <p>
          Wähle die Vertragspartner aus, die als Subunternehmer für <strong>{mainPartner?.partnerName}</strong>{' '}
          fungieren sollen.
        </p>
        {
          //small
          <p className="text-sm text-gray-500">
            Hinweis: Hier werden lediglich die Vertragspartner angezeigt, die in der Rolle "Ich bin der Auftraggeber"
            sind.
          </p>
        }

        <List>
          {principalPartners.length === 0 && (
            <ListItem>
              <ListItemText primary="Keine Vertragspartner mit Richtung 'Ich bin der Auftraggeber' gefunden." />
            </ListItem>
          )}
          {principalPartners.map((p) => (
            <ListItem key={p.index} divider sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, py: 1.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <ListItemText
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {p.partnerName}
                      <IconButton
                        size="small"
                        color={p.address ? 'primary' : 'default'}
                        onClick={() => {
                          const meta = getPartnerTableMetaByIndex(p.index);
                          console.log(meta);
                          setCurrentAddressMeta(meta);
                          setAddressDraft(p.address || '');
                          setEditAddressModalOpen(true);
                        }}
                        style={{ marginLeft: 4 }}
                      >
                        <LocationOnIcon style={{ opacity: p.address ? 1 : 0.5 }} />
                      </IconButton>
                    </span>
                  }
                  sx={{ m: 0 }}
                />
                <MuiTooltip
                  title={p.index === mainIndex ? 'Ein Partner kann sich nicht selbst als Subunternehmer zuweisen.' : ''}
                >
                  <span>
                    <Switch
                      checked={subIndices.includes(p.index)}
                      onChange={(_, checked) => handleToggle(p.index, checked)}
                      disabled={loading || localLoading === p.index || p.index === mainIndex}
                      inputProps={{ 'aria-label': `Subunternehmer für ${p.partnerName}` }}
                    />
                  </span>
                </MuiTooltip>
              </div>
              {subIndices.includes(p.index) && (
                <TextField
                  label="Details zur Datenweitergabe"
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  value={detailsDraft[p.index] || ''}
                  onChange={(e) => setDetailsDraft((prev) => ({ ...prev, [p.index]: e.target.value }))}
                  onBlur={(e) => handleDetailsBlur(p.index, e.target.value)}
                  placeholder="Optionale Angaben zu Art und Zweck der weitergegebenen Daten…"
                />
              )}
            </ListItem>
          ))}
        </List>
        <DownloadSubcontractorPdfButton
          partners={selectedPartners}
          caption={`Subunternehmer-Liste im Auftragsverarbeitungsverhältnis zwischen ${mainPartner?.partnerName} und ${user.company.name}`}
        />
      </div>
    </NotificationModal>
  );
}
